import React from "react";
import { motion } from "framer-motion";
import { Globe2, Volume2, BookOpen, Users2, ArrowUpRight } from "lucide-react";
import { T } from "@/context/LanguageContext";

const PILLARS = [
  { icon: Globe2, t: "Multilingual", d: "Every page translatable into 48+ international and Indian languages, with full right-to-left support." },
  { icon: Volume2, t: "Accessible", d: "Listen to any page with language-aware text-to-speech, playback controls and adjustable speed." },
  { icon: BookOpen, t: "Referenced", d: "Original USAPulse summaries linked to relevant Wikipedia and Wikidata articles — a reference layer, never a copy." },
  { icon: Users2, t: "Participatory", d: "Communities contribute local history, photos and stories in their own language, with clear provenance." },
];

const NETWORK = ["USAPulse", "WeatherPulse", "SciencePulse", "NaturePulse", "FarmersPulse"];

export function Pillars() {
  return (
    <section className="mx-auto max-w-[1500px] px-4 py-20">
      <div className="mb-12 max-w-2xl">
        <div className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-oldglory-red"><T>Four pillars</T></div>
        <h2 className="font-display text-4xl font-bold text-navy md:text-5xl"><T>Built for everyone, in every language</T></h2>
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {PILLARS.map((p, i) => (
          <motion.div
            key={p.t}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08, duration: 0.5 }}
            className="group rounded-xl border border-border bg-white p-7 transition-all hover:-translate-y-1 hover:border-navy/30 hover:shadow-xl"
          >
            <span className="inline-flex rounded-lg bg-navy/5 p-3 text-navy transition-colors group-hover:bg-oldglory-red group-hover:text-white">
              <p.icon className="h-6 w-6" strokeWidth={1.6} />
            </span>
            <h3 className="mt-5 font-display text-2xl font-semibold text-navy"><T>{p.t}</T></h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground"><T>{p.d}</T></p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export function PulseNetwork() {
  return (
    <section className="relative overflow-hidden bg-navy py-20 text-white">
      <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-oldglory-red/20 blur-3xl" />
      <div className="relative mx-auto max-w-[1500px] px-4">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-oldglory-red"><T>One platform, many products</T></div>
            <h2 className="font-display text-4xl font-bold leading-tight md:text-5xl">
              <T>The Pulse Network</T>
            </h2>
            <p className="mt-4 max-w-lg text-white/70">
              <T>USAPulse is one independently deployable, licensable product in a larger network — each with its own domain, branding and commerce, all sharing geography, language and AI services. A partner could operate 'Oregon Pulse' tomorrow without a new application.</T>
            </p>
            <button className="mt-8 inline-flex items-center gap-2 rounded-md bg-white px-6 py-3 font-semibold text-navy transition-colors hover:bg-parchment" data-testid="partner-cta">
              <T>Become a Partner</T> <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            {NETWORK.map((n, i) => (
              <motion.div
                key={n}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`rounded-xl px-6 py-5 font-display text-xl font-bold ring-1 ${n === "USAPulse" ? "bg-oldglory-red ring-oldglory-red" : "bg-white/5 ring-white/15"}`}
              >
                {n}
                <span className="mt-1 block text-[11px] font-sans font-normal text-white/50">
                  {n === "USAPulse" ? "Active" : "Ecosystem"}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
